import { reactive } from 'vue';
import { $api } from '@/main';
import cookieService from '@/services/cookieService';

interface User {
  id: number;
  pseudo: string;
  email: string;
  numero_telephone: string;
  adresse: string;
  ville: string;
  role: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// ✅ Lecture du token depuis localStorage ET cookies
const getToken = (): string | null => {
  return localStorage.getItem('client_token') || cookieService.getCookie('client_token');
};

const state = reactive<AuthState>({
  user: null,
  token: getToken(),
  isAuthenticated: !!getToken(),
  loading: false,
  error: null
});

export const useClientAuthStore = () => {
  const login = async (identifier: string, password: string) => {
    state.loading = true;
    state.error = null;

    try {
      const response = await $api.post('/api/login', {
        identifier,
        password
      });

      const { user, token } = response.data;

      localStorage.setItem('client_token', token);
      localStorage.setItem('client_user', JSON.stringify(user));
      
      if (cookieService.isCategoryAllowed('functional')) {
        cookieService.setCookie('client_token', token, 30);
        cookieService.setCookie('client_user', JSON.stringify(user), 30);
      }
      
      state.user = user;
      state.token = token;
      state.isAuthenticated = true;
      $api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return { success: true, user };
    } catch (error: any) {
      console.error('Erreur login:', error);
      state.error = error.response?.data?.message || error.message || 'Erreur de connexion';
      return { success: false, error: state.error };
    } finally {
      state.loading = false;
    }
  };

  const register = async (userData: any) => {
    state.loading = true;
    state.error = null;

    try {
      const response = await $api.post('/api/register', userData);
      const { user, token } = response.data;

      localStorage.setItem('client_token', token);
      localStorage.setItem('client_user', JSON.stringify(user));
      
      if (cookieService.isCategoryAllowed('functional')) {
        cookieService.setCookie('client_token', token, 30);
        cookieService.setCookie('client_user', JSON.stringify(user), 30);
      }
      
      state.user = user;
      state.token = token;
      state.isAuthenticated = true;
      $api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return { success: true, user };
    } catch (error: any) {
      state.error = error.response?.data?.message || 'Erreur d\'inscription';
      return { success: false, error: state.error };
    } finally {
      state.loading = false;
    }
  };

  const logout = async () => {
    try {
      if (state.token) {
        await $api.post('/api/logout');
      }
    } catch (error) {
      console.error('Erreur logout:', error);
    } finally {
      localStorage.removeItem('client_token');
      localStorage.removeItem('client_user');
      cookieService.deleteCookie('client_token');
      cookieService.deleteCookie('client_user');
      delete $api.defaults.headers.common['Authorization'];
      
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
    }
  };

  const checkAuth = async () => {
    const token = getToken();
    if (!token) {
      state.isAuthenticated = false;
      return false;
    }

    try {
      $api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await $api.get('/api/user');
      
      state.user = response.data;
      state.token = token;
      state.isAuthenticated = true;
      localStorage.setItem('client_user', JSON.stringify(response.data));
      
      return true;
    } catch (error) {
      console.error('Erreur vérification auth client:', error);
      await logout();
      return false;
    }
  };

  return {
    state,
    login,
    register,
    logout,
    checkAuth
  };
};